import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, X, ChevronRight, GraduationCap } from 'lucide-react';
import { LESSONS } from '../../data/lessons';
import { ModuleId } from '../../types';
import { useSfx } from '../../store/sfxStore';

interface ModuleLayoutProps {
    moduleId: ModuleId;
    title: string;
    description: string;
    children: React.ReactNode;
}

export const ModuleLayout: React.FC<ModuleLayoutProps> = ({ moduleId, title, description, children }) => {
    const lesson = LESSONS[moduleId];
    const [isLessonOpen, setLessonOpen] = useState(false);
    const { play } = useSfx();

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col pt-20">
            {/* Top Bar - Extension of Nav really, but Module specific */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center shadow-sm z-30 sticky top-16">
                <div>
                    <h1 className="text-xl font-bold text-slate-800">{title}</h1>
                    <p className="text-xs text-slate-500">{description}</p>
                </div>

                {lesson && (
                    <button
                        onClick={() => { setLessonOpen(!isLessonOpen); play('click'); }}
                        onMouseEnter={() => play('hover')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all ${isLessonOpen ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600'}`}
                    >
                        {isLessonOpen ? <X size={18} /> : <BookOpen size={18} />}
                        <span className="hidden sm:inline">{isLessonOpen ? 'Close Lesson' : 'Read Guide'}</span>
                    </button>
                )}
            </div>

            <div className="flex-1 flex overflow-hidden relative">

                {/* Lesson Sidebar (Slide Over) */}
                <AnimatePresence>
                    {isLessonOpen && lesson && (
                        <motion.div
                            initial={{ x: '100%', opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: '100%', opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="absolute inset-y-0 right-0 w-full md:w-[450px] bg-white shadow-2xl z-40 border-l border-slate-200 overflow-y-auto"
                        >
                            <div className="p-8">
                                <div className="flex items-center gap-3 text-indigo-600 mb-6">
                                    <GraduationCap size={28} />
                                    <span className="font-bold uppercase tracking-widest text-sm">Educational Material</span>
                                </div>

                                <h2 className="text-3xl font-black text-slate-800 mb-8 leading-tight">
                                    {lesson.title}
                                </h2>

                                <div className="space-y-8">
                                    {lesson.sections.map((section, idx) => (
                                        <div key={idx} className="prose prose-slate">
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-2">
                                                <span className="bg-indigo-100 text-indigo-600 w-6 h-6 rounded-full flex items-center justify-center text-xs">
                                                    {idx + 1}
                                                </span>
                                                {section.heading}
                                            </h3>
                                            <p className="text-slate-600 leading-relaxed text-sm">
                                                {section.content}
                                            </p>

                                            {section.keyTerms && (
                                                <div className="flex flex-wrap gap-2 mt-3">
                                                    {section.keyTerms.map(term => (
                                                        <span key={term} className="text-xs font-semibold bg-slate-100 text-slate-500 px-2 py-1 rounded border border-slate-200">
                                                            {term}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-12 bg-indigo-50 p-4 rounded-xl border border-indigo-100 text-center">
                                    <p className="text-indigo-800 font-medium text-sm mb-4">Ready to apply expected knowledge?</p>
                                    <button
                                        onClick={() => { setLessonOpen(false); play('click'); }}
                                        onMouseEnter={() => play('hover')}
                                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-lg font-bold"
                                    >
                                        Start Practice
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto bg-slate-50">
                    {children}
                </div>

            </div>
        </div>
    );
};
